import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { DownloadSimple, FileText, Bank as BankIcon, ShoppingCart, Archive, SpinnerGap, Info, CheckCircle, PaperPlaneTilt, X, Warning, Trash } from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);

export default function BoekhoudExportPage() {
  const now = new Date();
  const [jaar, setJaar] = useState(now.getFullYear());
  const [periode, setPeriode] = useState('ALL');
  const [overzicht, setOverzicht] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTo, setMailTo] = useState('');
  const [mailCcSelf, setMailCcSelf] = useState(true);
  const [mailOpmerking, setMailOpmerking] = useState('');
  const [mailSending, setMailSending] = useState(false);
  const [mailResult, setMailResult] = useState(null);
  const [integriteit, setIntegriteit] = useState(null);
  const [opruimBezig, setOpruimBezig] = useState(false);

  const laadOverzicht = async () => {
    setLoading(true);
    try {
      const r = await API.get(`/boekhoudexport/overzicht?jaar=${jaar}&periode=${periode}`);
      setOverzicht(r.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const laadIntegriteit = async () => {
    try {
      const r = await API.get('/boekhoudexport/integriteits-check');
      setIntegriteit(r.data);
    } catch (e) {
      console.error('integriteits-check faalde', e);
    }
  };

  useEffect(() => { laadOverzicht(); laadIntegriteit(); }, []); // eslint-disable-line

  const orphansOpruimen = async () => {
    if (!integriteit) return;
    const fac_ids = (integriteit.facturen_zonder_bestand || []).map(o => o.id);
    const doc_ids = (integriteit.documenten_zonder_bestand || []).map(o => o.id);
    if (!fac_ids.length && !doc_ids.length) return;
    if (!window.confirm(`Verwijder ${fac_ids.length} factuur/facturen en ${doc_ids.length} document(en) waarvoor het origineel-bestand ontbreekt? Dit is onomkeerbaar.`)) return;
    setOpruimBezig(true);
    try {
      await API.post('/boekhoudexport/orphans-opruimen', { factuur_ids: fac_ids, doc_ids });
      await laadIntegriteit();
      await laadOverzicht();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Opruimen mislukt');
    }
    setOpruimBezig(false);
  };

  const download = (endpoint) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/boekhoudexport/${endpoint}?jaar=${jaar}&periode=${periode}`;
    const a = document.createElement('a');
    a.href = url; a.target = '_blank'; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const openMailModal = async () => {
    setMailResult(null);
    setMailOpmerking('');
    // Prefill boekhouder-e-mail uit instellingen
    try {
      const r = await API.get('/instellingen');
      const inst = r.data || {};
      setMailTo(inst.boekhouder_email || inst.btw_reminder_email || '');
    } catch (e) {
      setMailTo('');
    }
    setMailOpen(true);
  };

  const verstuurNaarBoekhouder = async () => {
    if (!mailTo || !mailTo.includes('@')) {
      setMailResult({ ok: false, msg: 'Geef een geldig e-mailadres op.' });
      return;
    }
    setMailSending(true);
    setMailResult(null);
    try {
      const r = await API.post('/boekhoudexport/mail-naar-boekhouder', {
        jaar, periode, to: mailTo, cc_self: mailCcSelf, opmerking: mailOpmerking,
      });
      setMailResult({ ok: true, msg: `Verzonden naar ${r.data.to} — bijlage ${r.data.filename} (${r.data.size_kb} KB).` });
    } catch (e) {
      setMailResult({ ok: false, msg: e?.response?.data?.detail || 'Verzenden mislukt.' });
    }
    setMailSending(false);
  };

  const cards = overzicht ? [
    { key: 'verkoop', titel: 'Verkoopboek', sub: `${overzicht.verkoop.aantal} facturen + ${overzicht.creditnotas.aantal} creditnota's`, totaal: overzicht.verkoop.totaal + overzicht.creditnotas.totaal, color: '#059669', icon: ShoppingCart, bg: '#ECFDF5' },
    { key: 'aankoop', titel: 'Aankoopboek', sub: `${overzicht.aankoop.aantal} inkomende facturen`, totaal: overzicht.aankoop.totaal, color: '#D97706', icon: FileText, bg: '#FFFBEB' },
    { key: 'bank', titel: 'Bankboek', sub: `${overzicht.bank.aantal} bankverrichtingen`, totaal: overzicht.bank.totaal_in + overzicht.bank.totaal_uit, color: '#002FA7', icon: BankIcon, bg: '#F0F4FF' },
  ] : [];

  return (
    <div className="space-y-5" data-testid="boekhoudexport-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#002FA7] flex items-center gap-2"><Archive size={28} weight="bold" /> Boekhoudexport</h1>
      </div>

      {/* Periode selector */}
      <div className="section-card">
        <div className="p-5">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex flex-col">
              <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Jaar</label>
              <select className="field-input" style={{width: 140}} value={jaar} onChange={e => setJaar(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value))} data-testid="bh-jaar">
                <option value="ALL">Alle jaren</option>
                {Array.from({length: 6}).map((_, i) => {
                  const y = now.getFullYear() - i;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Periode</label>
              <select className="field-input" style={{width: 180}} value={periode} onChange={e => setPeriode(e.target.value)} data-testid="bh-periode">
                <option value="ALL">Volledig jaar</option>
                <optgroup label="Kwartaal">
                  <option value="Q1">Q1 (jan-mrt)</option>
                  <option value="Q2">Q2 (apr-jun)</option>
                  <option value="Q3">Q3 (jul-sep)</option>
                  <option value="Q4">Q4 (okt-dec)</option>
                </optgroup>
                <optgroup label="Maand">
                  {Array.from({length: 12}).map((_, i) => {
                    const m = String(i+1).padStart(2,'0');
                    const names = ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'];
                    return <option key={m} value={m}>{m} — {names[i]}</option>;
                  })}
                </optgroup>
              </select>
            </div>
            <div className="flex items-center gap-2 flex-wrap ml-auto">
              <button className="btn btn-primary inline-flex items-center gap-1.5" onClick={laadOverzicht} disabled={loading} data-testid="bh-laad">
                {loading ? <><SpinnerGap size={14} className="animate-spin" /> Laden...</> : 'Vernieuwen'}
              </button>
              {overzicht && (
                <>
                  <button className="btn bg-[#D97706] text-white font-bold hover:bg-[#B45309] inline-flex items-center gap-1.5" onClick={() => download('alles')} data-testid="bh-download-all">
                    <Archive size={14} weight="bold" /> Download alles (.zip)
                  </button>
                  <button className="btn bg-[#059669] text-white font-bold hover:bg-[#047857] inline-flex items-center gap-1.5" onClick={openMailModal} data-testid="bh-mail-boekhouder">
                    <PaperPlaneTilt size={14} weight="bold" /> Mail naar boekhouder
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
            <Info size={12} weight="bold" />
            CSV-formaat met UTF-8 BOM, puntkomma-separator en Belgische decimaalteken (,). Importeerbaar in <strong>Winbooks, Exact Online, Yuki, Odoo, Billit</strong> en andere boekhoudpakketten.
          </div>
        </div>
      </div>

      {/* Integriteit-waarschuwing — facturen zonder origineel bestand */}
      {integriteit && integriteit.totaal_orphans > 0 && (
        <div className="section-card" style={{ borderLeft: '4px solid #DC2626' }} data-testid="bh-orphans-banner">
          <div className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-2.5">
                <Warning size={22} weight="bold" className="text-[#DC2626] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-[#DC2626] text-sm">{integriteit.totaal_orphans} record{integriteit.totaal_orphans === 1 ? '' : 's'} zonder origineel bestand</div>
                  <div className="text-xs text-gray-700 mt-1">
                    Deze facturen/documenten staan in de database, maar het oorspronkelijke PDF/XML-bestand ontbreekt op de server.
                    Uw boekhouder kan ze niet nazien. Verwijder ze of upload het bestand opnieuw via Inkomende documenten.
                  </div>
                  <details className="mt-2">
                    <summary className="text-xs text-[#DC2626] cursor-pointer hover:underline font-bold">Toon details</summary>
                    <div className="mt-2 text-[11px] text-gray-600 space-y-1 max-h-40 overflow-auto">
                      {(integriteit.facturen_zonder_bestand || []).map(o => (
                        <div key={o.id} className="font-mono">FACTUUR · {o.datum || '?'} · {o.leverancier} · {o.factuurnummer} · €{(o.totaal || 0).toFixed(2)}</div>
                      ))}
                      {(integriteit.documenten_zonder_bestand || []).map(o => (
                        <div key={o.id} className="font-mono">DOC · {o.filename || '(geen bestandsnaam)'} · stored={o.stored || '(leeg)'}</div>
                      ))}
                    </div>
                  </details>
                </div>
              </div>
              <button
                className="btn btn-sm bg-[#DC2626] text-white font-bold hover:bg-[#B91C1C] inline-flex items-center gap-1.5 flex-shrink-0"
                onClick={orphansOpruimen}
                disabled={opruimBezig}
                data-testid="bh-orphans-cleanup"
              >
                {opruimBezig ? <><SpinnerGap size={13} className="animate-spin" /> Opruimen...</> : <><Trash size={13} weight="bold" /> Opruimen</>}
              </button>
            </div>
          </div>
        </div>
      )}
      {integriteit && integriteit.totaal_orphans === 0 && (
        <div className="p-2.5 bg-green-50 border border-green-200 text-xs text-green-800 flex items-center gap-2" data-testid="bh-integriteit-ok">
          <CheckCircle size={14} weight="bold" /> Alle facturen en documenten hebben een geldig origineel bestand.
        </div>
      )}

      {/* Cards per boek */}
      {overzicht && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cards.map(c => (
            <div key={c.key} className="section-card" style={{ borderLeft: `4px solid ${c.color}` }}>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: c.bg }}>
                      <c.icon size={20} weight="bold" style={{ color: c.color }} />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-gray-800">{c.titel}</div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider">{c.sub}</div>
                    </div>
                  </div>
                </div>
                <div className="text-2xl font-black" style={{ color: c.color }} data-testid={`bh-totaal-${c.key}`}>{fmt(c.totaal)}</div>
                <button className="mt-3 w-full btn btn-sm text-white font-bold inline-flex items-center justify-center gap-1.5" style={{ background: c.color }} onClick={() => download(c.key)} data-testid={`bh-download-${c.key}`}>
                  <DownloadSimple size={13} weight="bold" /> Download CSV
                </button>
                {(c.key === 'verkoop' || c.key === 'aankoop') && (
                  <button className="mt-2 w-full btn btn-sm bg-white font-bold inline-flex items-center justify-center gap-1.5 border" style={{ borderColor: c.color, color: c.color }} onClick={() => download(`${c.key}-pdfs`)} data-testid={`bh-download-${c.key}-pdfs`}>
                    <FileText size={13} weight="bold" /> {c.key === 'verkoop' ? 'Download alle PDF facturen' : 'Download originele bestanden'}
                  </button>
                )}
                {c.key === 'bank' && (
                  <button
                    className="mt-2 w-full btn btn-sm bg-white font-bold inline-flex items-center justify-center gap-1.5 border"
                    style={{ borderColor: c.color, color: c.color }}
                    onClick={() => download('bank-coda')}
                    data-testid="bh-download-bank-coda"
                    title="Exporteer als officieel Belgisch CODA v2.3 formaat — importeerbaar in Winbooks, Octopus, BOB, Sage en de meeste Belgische boekhoudpakketten"
                  >
                    <FileText size={13} weight="bold" /> Download CODA (.cod)
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uitleg kolommen */}
      <div className="section-card">
        <div className="section-header"><span className="section-title">Wat zit er in elk bestand?</span></div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5 text-xs">
          <div>
            <div className="font-bold text-[#059669] mb-2 flex items-center gap-1"><ShoppingCart size={14} weight="bold" /> Verkoopboek</div>
            <div className="text-gray-600 space-y-1">
              <div>• Documentnr, Datum, Vervaldag</div>
              <div>• Klant naam, BTW-nummer, adres</div>
              <div>• Bedrag excl/incl/BTW</div>
              <div>• OGM, Mededeling, Referentie</div>
              <div>• Grootboek klant, Status (betaald/vervallen)</div>
              <div>• Creditnota's: negatieve bedragen</div>
            </div>
          </div>
          <div>
            <div className="font-bold text-[#D97706] mb-2 flex items-center gap-1"><FileText size={14} weight="bold" /> Aankoopboek</div>
            <div className="text-gray-600 space-y-1">
              <div>• Factuurnummer, Datum, Vervaldag</div>
              <div>• Leverancier, BTW-nr, IBAN</div>
              <div>• Categorie + Grootboekrekening</div>
              <div>• BTW%, Aftrekbaarheid%</div>
              <div>• Bedrag excl/incl/BTW</div>
              <div>• Bronbestand (PDF-naam)</div>
            </div>
          </div>
          <div>
            <div className="font-bold text-[#002FA7] mb-2 flex items-center gap-1"><BankIcon size={14} weight="bold" /> Bankboek</div>
            <div className="text-gray-600 space-y-1">
              <div>• Datum, Tegenpartij, IBAN</div>
              <div>• Mededeling (OGM herkend)</div>
              <div>• Bedrag, Valuta</div>
              <div>• Type (Inkomend/Uitgaand)</div>
              <div>• Gekoppelde factuur (ID)</div>
              <div>• Status (open/afgeletterd)</div>
              <div className="pt-1 mt-1 border-t border-gray-200 text-[10px] text-[#002FA7] italic">
                <strong>CODA (.cod):</strong> officieel Belgisch bank-uittreksel<br />formaat (Febelfin v2.3) — importeerbaar in<br />Winbooks, Octopus, BOB, Sage en ieder<br />Belgisch boekhoudpakket.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-blue-50 border border-blue-200 text-xs text-blue-800">
        <CheckCircle size={14} weight="bold" className="inline mr-1" />
        <strong>Tip voor uw boekhouder:</strong> mail deze 3 CSV's (of de .zip) door, en hij/zij kan alles in één keer importeren in Winbooks/Exact/Yuki. Geen enkel cijfer hoeft nog manueel getypt te worden — elke factuur komt met BTW%, grootboekrekening en categorie reeds correct gelabeld.
      </div>

      {/* Mail naar boekhouder modal */}
      {mailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !mailSending && setMailOpen(false)} data-testid="mail-boekhouder-modal">
          <div className="bg-white rounded-md shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div className="flex items-center gap-2">
                <PaperPlaneTilt size={18} weight="bold" className="text-[#059669]" />
                <span className="font-bold text-gray-800">Mail boekhoudexport naar boekhouder</span>
              </div>
              <button className="text-gray-400 hover:text-gray-700" onClick={() => !mailSending && setMailOpen(false)} data-testid="mail-boekhouder-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-gray-50 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Jaar</div>
                  <div className="font-bold">{jaar}</div>
                </div>
                <div className="bg-gray-50 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Periode</div>
                  <div className="font-bold">{periode === 'ALL' ? 'Volledig jaar' : periode}</div>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">E-mailadres boekhouder</label>
                <input className="field-input w-full" type="email" value={mailTo} onChange={e => setMailTo(e.target.value)} placeholder="boekhouder@voorbeeld.be" data-testid="mail-boekhouder-to" />
                <div className="text-[11px] text-gray-500 mt-1">Wordt voorgevuld op basis van <strong>Instellingen → BTW-aangifte herinnering</strong>.</div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Opmerking (optioneel)</label>
                <textarea className="field-input w-full" rows={3} value={mailOpmerking} onChange={e => setMailOpmerking(e.target.value)} placeholder="Bv. Gelieve deze stukken te verwerken voor de kwartaalaangifte." data-testid="mail-boekhouder-note" />
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={mailCcSelf} onChange={e => setMailCcSelf(e.target.checked)} data-testid="mail-boekhouder-cc" />
                <span>Kopie (CC) naar mijn afzenderadres</span>
              </label>
              {mailResult && (
                <div className={`text-xs p-2 ${mailResult.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="mail-boekhouder-result">
                  {mailResult.msg}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50">
              <button className="btn btn-sm" onClick={() => setMailOpen(false)} disabled={mailSending} data-testid="mail-boekhouder-cancel">Annuleren</button>
              <button className="btn btn-sm bg-[#059669] text-white font-bold hover:bg-[#047857] inline-flex items-center gap-1.5" onClick={verstuurNaarBoekhouder} disabled={mailSending} data-testid="mail-boekhouder-send">
                {mailSending ? <><SpinnerGap size={13} className="animate-spin" /> Verzenden...</> : <><PaperPlaneTilt size={13} weight="bold" /> Verstuur nu</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
